import { useCallback, useEffect, useState } from 'react';

import { deleteFromHistory, fetchHistory, saveToHistory } from '../lib/api.js';

/**
 * The signed-in user's saved tickers.
 *
 * Reads and writes go through the API (which verifies the JWT and stores
 * `user_id` from that token), so this hook only manages loading state and the
 * optimistic removal of rows.
 *
 * @param {string | null} token Neon Auth access token, or null when signed out
 */
export function useHistory(token) {
  const [state, setState] = useState({ status: 'idle', items: [], error: null });

  const reload = useCallback(async () => {
    if (!token) {
      setState({ status: 'idle', items: [], error: null });
      return;
    }
    setState((previous) => ({ ...previous, status: 'loading' }));
    try {
      const items = await fetchHistory(token);
      setState({ status: 'ready', items, error: null });
    } catch (error) {
      setState({ status: 'error', items: [], error: error.message });
    }
  }, [token]);

  useEffect(() => {
    reload();
  }, [reload]);

  const save = useCallback(
    async (ticker) => {
      if (!token) throw new Error('Sign in to save tickers');
      await saveToHistory(token, ticker);
      await reload();
    },
    [token, reload],
  );

  const remove = useCallback(
    async (ticker) => {
      if (!token) return;
      setState((previous) => ({
        ...previous,
        items: previous.items.filter((item) => item.ticker !== ticker),
      }));
      try {
        await deleteFromHistory(token, ticker);
      } finally {
        await reload();
      }
    },
    [token, reload],
  );

  return { ...state, reload, save, remove };
}
